/**
 * Centralised API paths.
 *
 * Keeping every path in one place makes a future endpoint rename a
 * single-file change.
 */

import type { Site } from '@/entities/site'

export const EZ_JWT_BASE = '/wp-json/ez-jwt/v1'

/**
 * Build the standard WP REST root for a given site on a multisite
 * network. Each subsite mounts wp/v2 at its own path:
 * `https://dev.ez-qr.menu/test-import-site/wp-json/wp/v2/...`.
 *
 * We pass `site.path` straight through — the server set it (e.g.
 * `/test-import-site/`) and our apiClient uses an origin-less baseURL,
 * so the path goes into the proxy on the same origin.
 */
export function wpV2Base(site: Pick<Site, 'path'>): string {
  // Normalise: ensure exactly one leading slash, no trailing slash, then
  // append the REST namespace. `'' | '/'` both yield `/wp-json/wp/v2`.
  const path = site.path === '/' || site.path === '' ? '' : site.path.replace(/\/+$/, '')
  return `${path}/wp-json/wp/v2`
}

export const wpV2 = {
  dishes: (site: Pick<Site, 'path'>) => `${wpV2Base(site)}/ezdish`,
  dish: (site: Pick<Site, 'path'>, id: number) => `${wpV2Base(site)}/ezdish/${id}`,
  /**
   * WP-standard revisions endpoint. Authenticated reads only — requires
   * `edit_post` on the parent. CPTs with `'revisions'` in their
   * `supports` array get this automatically; the `ezqr` plugin
   * registers `ezdish` with revisions on by default.
   */
  dishRevisions: (site: Pick<Site, 'path'>, id: number) =>
    `${wpV2Base(site)}/ezdish/${id}/revisions`,
  categories: (site: Pick<Site, 'path'>) => `${wpV2Base(site)}/ezcategory`,
  category: (site: Pick<Site, 'path'>, id: number) => `${wpV2Base(site)}/ezcategory/${id}`,
  tags: (site: Pick<Site, 'path'>) => `${wpV2Base(site)}/eztag`,
  tag: (site: Pick<Site, 'path'>, id: number) => `${wpV2Base(site)}/eztag/${id}`,
  media: (site: Pick<Site, 'path'>) => `${wpV2Base(site)}/media`,
  mediaItem: (site: Pick<Site, 'path'>, id: number) => `${wpV2Base(site)}/media/${id}`,
  pages: (site: Pick<Site, 'path'>) => `${wpV2Base(site)}/pages`,
  page: (site: Pick<Site, 'path'>, id: number) => `${wpV2Base(site)}/pages/${id}`,
  /** Shared modifier CPT (each post = one modifier, has `_crb_price`). */
  sharedMods: (site: Pick<Site, 'path'>) => `${wpV2Base(site)}/ezmod`,
  sharedMod: (site: Pick<Site, 'path'>, id: number) => `${wpV2Base(site)}/ezmod/${id}`,
  /** Shared modifier group taxonomy (terms group ezmod + ezdish). */
  sharedModGroups: (site: Pick<Site, 'path'>) => `${wpV2Base(site)}/ezmodgroup`,
  sharedModGroup: (site: Pick<Site, 'path'>, id: number) => `${wpV2Base(site)}/ezmodgroup/${id}`,
  /**
   * Current user. Site-scoped only because WP routes everything under
   * a site; in multisite the user identity is shared so it doesn't
   * matter which site's path we hit — we just need one.
   */
  usersMe: (site: Pick<Site, 'path'>) => `${wpV2Base(site)}/users/me`,
} as const

/**
 * Custom `ezqr/v2` namespace for the SPA's own endpoints. The plugin
 * is network-activated, so the namespace is mounted at the network
 * root (no per-site path prefix) — see docs/BULK_DISHES_BACKEND.md.
 * `site_id` (numeric blog id) goes in the path, not `site.path`.
 */
export const EZQR_V2_BASE = '/wp-json/ezqr/v2'

export const ezqrV2 = {
  bulkDishes: (siteId: number) => `${EZQR_V2_BASE}/sites/${siteId}/bulk/dishes`,
  bulkDishPrices: (siteId: number) => `${EZQR_V2_BASE}/sites/${siteId}/bulk/dishes/price`,
} as const

/**
 * `ezqr/v1` lives **per subsite** (mounted on each blog like `wp/v2`),
 * unlike `ezqr/v2` which is network-rooted. The capability endpoint
 * is the canonical "what does this site have, and how is it
 * configured" source — see
 * memory/site-capabilities-contract.md (and the entity in
 * src/entities/site-capabilities.ts) for the envelope.
 */
export function ezqrV1Base(site: Pick<Site, 'path'>): string {
  const path = site.path === '/' || site.path === '' ? '' : site.path.replace(/\/+$/, '')
  return `${path}/wp-json/ezqr/v1`
}

export const ezqrV1 = {
  site: (site: Pick<Site, 'path'>) => `${ezqrV1Base(site)}/site`,
} as const

/**
 * Build a full subsite-scoped REST path from a relative namespace
 * path (e.g. `'ez-clover/v1/settings/catalog-mode'`). Used when the
 * endpoint URL isn't hardcoded — the capabilities envelope (see
 * memory/site-capabilities-contract.md) hands relative paths to the
 * SPA and we resolve them against the current site here.
 */
export function wpJsonPath(site: Pick<Site, 'path'>, relativeNamespacePath: string): string {
  const path = site.path === '/' || site.path === '' ? '' : site.path.replace(/\/+$/, '')
  const rel = relativeNamespacePath.replace(/^\/+/, '')
  return `${path}/wp-json/${rel}`
}

export const authPaths = {
  login: `${EZ_JWT_BASE}/auth/login`,
  refresh: `${EZ_JWT_BASE}/auth/refresh`,
  logout: `${EZ_JWT_BASE}/auth/logout`,
  me: `${EZ_JWT_BASE}/auth/me`,
} as const

/**
 * Paths that must NOT carry a Bearer header — they either authenticate the
 * user (login) or rely on the refresh cookie alone (refresh, logout).
 *
 * `refresh` and `logout` may carry a Bearer in mobile flows; we still skip
 * it on the client to keep web/mobile request bodies identical, the cookie
 * is the source of truth here.
 */
export const AUTH_OPEN_PATHS = [authPaths.login, authPaths.refresh, authPaths.logout] as const

export function isAuthOpenPath(path: string): boolean {
  return AUTH_OPEN_PATHS.some((p) => path === p || path.startsWith(`${p}?`))
}

/**
 * Paths that require the cross-site credentials cookie (refresh + CSRF).
 * Used by the client to set `credentials: 'include'` selectively.
 */
export function requiresCookies(path: string): boolean {
  return (
    path === authPaths.login ||
    path === authPaths.refresh ||
    path === authPaths.logout ||
    path.startsWith(`${authPaths.login}?`) ||
    path.startsWith(`${authPaths.refresh}?`) ||
    path.startsWith(`${authPaths.logout}?`)
  )
}
